import { useState } from "react";
import { Check, Loader2 } from "lucide-react";

const FORMS_URL = "https://forms.elido.nu/api/contact";
const SITE_ID = "folkare-bygg";

const PROJECT_TYPES = [
  "Reparation & ombyggnad",
  "Tillbyggnad",
  "Fasad & tak",
  "Fönster & dörrar",
  "Kök & badrum",
  "Varsam renovering av äldre hus",
  "Annat / osäker",
];

const EMPTY = {
  namn: "",
  telefon: "",
  epost: "",
  beskrivning: "",
  service: "",
  ort: "",
  website: "",
};

const validate = (v) => {
  const e = {};
  if (!v.namn.trim()) e.namn = "Skriv ditt namn.";

  const mail = v.epost.trim();
  if (!mail) e.epost = "Fyll i din e-postadress.";
  else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(mail))
    e.epost = "Kontrollera e-postadressen.";

  const tel = v.telefon.trim();
  if (tel && !/^\+?[\d\s-]{7,}$/.test(tel))
    e.telefon = "Kontrollera telefonnumret.";

  if (!v.service) e.service = "Välj vilken typ av arbete det gäller.";
  if (!v.ort.trim()) e.ort = "Ange var arbetet ska utföras.";
  if (!v.beskrivning.trim()) e.beskrivning = "Beskriv kort vad du vill ha hjälp med.";

  return e;
};

const Field = ({ id, label, hint, error, as = "input", required = false, ...props }) => {
  const Tag = as;
  return (
    <div>
      <label
        htmlFor={`f-${id}`}
        className="flex items-baseline justify-between text-sm font-semibold text-ink"
      >
        <span>{label}{required && <span className="ml-1 text-moss" aria-hidden="true">*</span>}</span>
        {hint && <span className="text-xs font-normal text-ink3">{hint}</span>}
      </label>
      <Tag
        id={`f-${id}`}
        name={id}
        required={required}
        aria-invalid={!!error}
        aria-describedby={error ? `f-${id}-err` : undefined}
        className={`mt-2 block w-full border bg-paper px-5 py-4 text-[1.02rem] text-ink placeholder:text-ink3/70 transition-colors focus:border-moss focus:outline-none focus:ring-1 focus:ring-moss sm:min-h-[56px] ${error ? "border-[#9B3B2E]" : "border-line hover:border-ink3"}`}
        {...props}
      />
      {error && (
        <p id={`f-${id}-err`} className="mt-2 text-sm text-[#9B3B2E]">
          {error}
        </p>
      )}
    </div>
  );
};

export const ContactForm = () => {
  const [values, setValues] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [submissionId, setSubmissionId] = useState(() => typeof window === "undefined" ? "" : crypto.randomUUID());

  const onChange = (e) => {
    const next = { ...values, [e.target.name]: e.target.value };
    setValues(next);
    setSubmissionId(crypto.randomUUID());
    if (Object.keys(errors).length) setErrors(validate(next));
    if (submitError) setSubmitError("");
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (sending) return;

    const errs = validate(values);
    setErrors(errs);
    if (Object.keys(errs).length) {
      document.getElementById(`f-${Object.keys(errs)[0]}`)?.focus();
      return;
    }

    setSending(true);
    setSubmitError("");

    try {
      const response = await fetch(FORMS_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          site_id: SITE_ID,
          submission_id: submissionId,
          website: values.website,
          subject: `Offertförfrågan – ${values.service}`,
          name: values.namn.trim(),
          email: values.epost.trim(),
          phone: values.telefon.trim() || null,
          service: values.service,
          city: values.ort.trim(),
          message: values.beskrivning.trim(),
        }),
      });

      const payload = await response.json().catch(() => ({}));

      if (!response.ok || !payload?.ok || !payload?.emailed) {
        throw new Error("request_failed");
      }

      setSent(true);
      setValues(EMPTY);
      setSubmissionId(crypto.randomUUID());

      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: "contact_form_success",
        method: "web_form",
        site_id: SITE_ID,
      });
    } catch {
      setSubmitError(
        "Något gick fel när förfrågan skulle skickas. Försök igen eller ring Lars på 070-575 14 83."
      );
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <div
        id="kontaktformular"
        role="status"
        aria-live="polite"
        className="border border-line bg-paper p-8 sm:p-12"
      >
        <span className="inline-flex h-12 w-12 items-center justify-center bg-moss text-paper">
          <Check size={22} aria-hidden="true" />
        </span>
        <h3 className="mt-6 font-display text-3xl text-ink">
          Tack för din förfrågan!
        </h3>
        <p className="mt-3 max-w-md text-base leading-relaxed text-ink2">
          Meddelandet är skickat. Lars hör av sig så snart han kan.
        </p>
        <button
          type="button"
          onClick={() => setSent(false)}
          className="link-underline mt-8 text-sm font-semibold text-moss"
        >
          Skicka en ny förfrågan
        </button>
      </div>
    );
  }

  return (
    <form
      id="kontaktformular"
      noValidate
      onSubmit={onSubmit}
      className="border border-line bg-paper p-7 sm:p-10 lg:p-12 xl:p-14"
      aria-labelledby="form-title"
    >
      <div className="hidden" aria-hidden="true">
        <label htmlFor="f-website">Lämna detta fält tomt</label>
        <input
          id="f-website"
          name="website"
          tabIndex={-1}
          autoComplete="off"
          value={values.website}
          onChange={onChange}
        />
      </div>

      <p className="eyebrow">Offertförfrågan</p>
      <h3 id="form-title" className="mt-3 font-display text-3xl text-ink sm:text-[2rem]">
        Skicka din förfrågan
      </h3>
      <p className="mt-3 text-sm leading-relaxed text-ink3">
        Berätta lite om projektet så återkommer Lars.
      </p>

      <fieldset className="mt-9">
        <legend className="w-full border-b border-line pb-3 text-sm font-semibold text-ink">
          <span className="mr-3 text-moss">01</span> Dina kontaktuppgifter
        </legend>
        <div className="mt-5 grid gap-5 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <Field
              id="namn"
              label="Namn"
              autoComplete="name"
              value={values.namn}
              onChange={onChange}
              error={errors.namn}
              required
            />
          </div>
          <Field
            id="telefon"
            label="Telefon"
            hint="Valfritt"
            type="tel"
            autoComplete="tel"
            inputMode="tel"
            placeholder="070-123 45 67"
            value={values.telefon}
            onChange={onChange}
            error={errors.telefon}
          />
          <Field
            id="epost"
            label="E-post"
            type="email"
            autoComplete="email"
            placeholder="namn@exempel.se"
            value={values.epost}
            onChange={onChange}
            error={errors.epost}
            required
          />
        </div>
      </fieldset>

      <fieldset className="mt-9">
        <legend className="w-full border-b border-line pb-3 text-sm font-semibold text-ink">
          <span className="mr-3 text-moss">02</span> Om byggprojektet
        </legend>
        <div className="mt-5 grid gap-5 sm:grid-cols-2">
          <Field
            id="service"
            label="Typ av arbete"
            as="select"
            value={values.service}
            onChange={onChange}
            error={errors.service}
            required
          >
            <option value="">Välj typ av arbete</option>
            {PROJECT_TYPES.map((item) => (
              <option key={item} value={item}>{item}</option>
            ))}
          </Field>
          <Field
            id="ort"
            label="Ort där arbetet ska utföras"
            placeholder="T.ex. Avesta eller Hedemora"
            autoComplete="address-level2"
            value={values.ort}
            onChange={onChange}
            error={errors.ort}
            required
          />
          <div className="sm:col-span-2">
            <Field
              id="beskrivning"
              label="Beskriv ditt projekt"
              as="textarea"
              rows={5}
              maxLength={3000}
              placeholder="Vad vill du ha hjälp med? Berätta gärna ungefär när du vill komma igång."
              value={values.beskrivning}
              onChange={onChange}
              error={errors.beskrivning}
              required
            />
          </div>
        </div>
      </fieldset>

      <p className="mt-7 max-w-2xl text-xs leading-relaxed text-ink3">
        Fält märkta * är obligatoriska. Genom att skicka förfrågan godkänner
        du att uppgifterna används för att besvara ditt meddelande.
      </p>

      {submitError && (
        <p role="alert" className="mt-5 border-l-2 border-[#9B3B2E] bg-white px-4 py-3 text-sm text-[#9B3B2E]">
          {submitError}
        </p>
      )}

      <button
        type="submit"
        disabled={sending}
        className="btn-primary mt-7 w-full !px-8 !py-4 disabled:cursor-wait disabled:opacity-60 sm:w-auto"
      >
        {sending ? (
          <>
            <Loader2 size={16} className="animate-spin" aria-hidden="true" />
            Skickar…
          </>
        ) : (
          "Skicka offertförfrågan"
        )}
      </button>
    </form>
  );
};
